import type { operations } from '@luxuryescapes/contract-trip/server'
import type { definitions, paths } from '@luxuryescapes/contract-trip'
import type { Moment } from 'moment'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'

import type {
  AcceptInviteResponse,
  AddBasicActivityToTripRequest,
  AddBasicActivityToTripResponse,
  ChatListResponse,
  ChatSearchPageResponse,
  ClaimChatsRequest,
  ClaimChatsResponse,
  CreateTripRequest,
  CreateTripResponse,
  DeleteChatResponse,
  DeleteInviteResponse,
  DeleteTripResponse,
  SubmitMessageFeedbackRequest,
  SubmitMessageFeedbackResponse,
  EditCollaboratorRequest,
  EditCollaboratorResponse,
  EditInviteRequest,
  EditInviteResponse,
  GetLowStockTripItemsResponse,
  GetPlaceDetailResponse,
  GetPlaceDetailResult,
  GetPlaceInfoResult,
  GetTripCollaboratorsResponse,
  GetTripMetadataResponse,
  GetTripResponse,
  GetTripsResponse,
  GetPlaceAreasResult,
  GetPlaceBestTimeToVisitResult,
  GetPlaceInsiderTipsResult,
  GetPlaceOverviewResult,
  GetPlaceWhatToLoveResult,
  PlaceInfoImageMode,
  RecordTripViewResponse,
  RefreshTripResponse,
  RegenerateDayAiRequest,
  RegenerateDayAiResponse,
  RegenerateItemAiRequest,
  RegenerateItemAiResponse,
  RemoveCollaboratorResponse,
  SendInviteRequest,
  SendInviteResponse,
  TripPlannerChat,
  TripPlannerChatDetails,
  CreateChatRequest,
  GenerateChatTitleParams,
  GenerateChatTitleRequest,
  GenerateChatTitleResponse,
  GenerateSalesAgentSummaryResponse,
  ManualItineraryDeleteRequest,
  ManualItineraryDeleteResponse,
  ManualItineraryMoveRequest,
  ManualItineraryMoveResponse,
  UpdateChatRequest,
  UpdateChatResponse,
  UndoRegenerateDayAiRequest,
  UndoRegenerateDayAiResponse,
  UpdateTravelPreferencesRequest,
  UpdateTravelPreferencesResponse,
  UpdateTripRequest,
  UpdateTripResponse,
  UploadTripImageRequest,
  UploadTripImageResponse,
  GetItineraryResponse,
  LocationDrawImagesRequest,
  LocationDrawImagesResponse,
  LocationDrawInformationRequest,
  PlaceDrawDetailsRequest,
  PlaceDrawDetailsResponse,
  HotelSearchResult,
  OfferDetailResponse,
  OfferDetailResult,
  HotelRoomOptionsRequest,
  HotelRoomOptionsResponse,
  HotelRoomOptionsResult,
  GetOfferPropertySurroundingsResponse,
  OfferReviewsResponse,
  ExperienceDetailResponse,
  ExperienceDetailResult,
  ExperienceSearchResponse,
  ExperienceSearchResult,
  ExperienceSearchFallbackPlace,
  OfferReviewsResult,
  SwitchDefaultTransportResponse,
  TripChatRecentSearch,
  TripItineraryData,
  UpdateTripChatItemRequest,
  UpdateTripChatItemResponse,
} from './types'
import type { UpsertTripChatRecentSearchBody } from 'tripPlanner/utils/tripChatRecentSearchPayload'
import { toTripItineraryData } from 'tripPlanner/utils/mapTripItineraryData'
import { baseUri, getOptions, extractApiErrorMessage } from './utils'

import request from 'api/requestUtils'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { serialiseOccupancy } from 'lib/search/occupancy'
import { SYDNEY_PLACE_INFO } from 'tripPlanner/config'
import type { TravellerRoom } from 'tripPlanner/types/common'

export type { ChatMessageAttachment } from './types'
export { fileToChatMessageAttachment, toChatAttachmentSummary } from './chatUtils'

export interface UploadFileResponse {
  id: string
  filename: string
}

export type PromptPresetResponse = {
  name: string
  promptId: string
  notes?: string | null
}

export function getTrip(
  tripId: string,
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const uri = `${baseUri}/${tripId}`
  return request
    .get<GetTripResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getTripMetadata(
  tripId: string,
  accessToken?: string,
): Promise<definitions['tripMetadata']> {
  const uri = `${baseUri}/${tripId}/metadata`
  return request
    .get<GetTripMetadataResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function refreshTripOrders(
  tripId: string,
): Promise<definitions['refreshedTrip']> {
  const uri = `${baseUri}/${tripId}/refresh`
  return request
    .post<RefreshTripResponse, undefined>(uri, undefined, getOptions())
    .then((response) => response.result)
}

export function recordTripView(tripId: string): Promise<null> {
  const uri = `${baseUri}/${tripId}/record-view`
  return request
    .post<RecordTripViewResponse, undefined>(uri, undefined, getOptions())
    .then((response) => response.result)
}

export function getTrips(
  accessToken?: string,
): Promise<Array<definitions['basicTrip']>> {
  return request
    .get<GetTripsResponse>(baseUri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getTripCollaborators(
  tripId: string,
  accessToken?: string,
): Promise<definitions['tripCollaborators']> {
  const uri = `${baseUri}/${tripId}/collaborators`
  return request
    .get<GetTripCollaboratorsResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function sendTripInvite(
  {
    tripId,
    email,
    role,
  }: {
    tripId: string
    email: string
    role: SendInviteRequest['role']
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/invitation`
  return request
    .post<SendInviteResponse, SendInviteRequest>(
      uri,
      { email, role },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function acceptTripInvite(
  {
    tripId,
    inviteId,
  }: {
    tripId: string
    inviteId: string
  },
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const uri = `${baseUri}/${tripId}/invitations/${inviteId}/accept`
  return request
    .post<AcceptInviteResponse, undefined>(
      uri,
      undefined,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function deleteTripInvite(
  {
    tripId,
    invitationId,
  }: {
    tripId: string
    invitationId: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/invitations/${invitationId}`
  return request
    .delete<DeleteInviteResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function removeCollaborator(
  {
    tripId,
    userId,
  }: {
    tripId: string
    userId: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/collaborators/${userId}`
  return request
    .delete<RemoveCollaboratorResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function editCollaborator(
  {
    tripId,
    userId,
    role,
  }: {
    tripId: string
    userId: string
    role: SendInviteRequest['role']
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/collaborators/${userId}`
  return request
    .patch<EditCollaboratorResponse, EditCollaboratorRequest>(
      uri,
      { role },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function editTripInvite(
  {
    tripId,
    invitationId,
    role,
  }: {
    tripId: string
    invitationId: string
    role: SendInviteRequest['role']
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/invitations/${invitationId}`
  return request
    .patch<EditInviteResponse, EditInviteRequest>(
      uri,
      { role },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function deleteTrip(id: string, accessToken?: string): Promise<null> {
  const uri = `${baseUri}/${id}`
  return request
    .delete<DeleteTripResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

interface CreateArgs {
  name: string
  startDate?: Moment
  endDate?: Moment
  imageId?: string
}

interface BaseTripActivity {
  name: string
  image: Array<string>
  city: string
  price: number
  currency: string
  availability: Array<string>
  category: Array<string>
  place: {
    type: string
    name: string
    address: string
    region: string
    point: {
      lat: number
      lng: number
    }
    photoUrl: string
  }
}

export interface LEExperienceTripActivity extends BaseTripActivity {
  offerId: string
}

export interface NonLEExperienceTripActivity extends BaseTripActivity {
  placeId: string
}

export function createChat(body?: CreateChatRequest): Promise<TripPlannerChat> {
  const uri = `${baseUri}/v2/chats`
  return request
    .post<App.ApiResponse<TripPlannerChat>, CreateChatRequest | undefined>(uri, body, { ...getOptions() })
    .then((response) => response.result)
}

export function submitMessageFeedback(
  chatId: number,
  messageId: string,
  reaction: 'UP' | 'DOWN',
  accessToken?: string,
): Promise<SubmitMessageFeedbackResponse> {
  const uri = `${baseUri}/v2/chats/${chatId}/messages/${messageId}/feedback`
  return request
    .post<App.ApiResponse<SubmitMessageFeedbackResponse>, SubmitMessageFeedbackRequest>(
      uri,
      { reaction },
      { ...getOptions(accessToken) },
    )
    .then(response => response.result)
}

export function regenerateIndividualItem(
  params: RegenerateItemAiRequest,
  tripId: string,
  accessToken?: string,
): Promise<RegenerateItemAiResponse['result']['activities']> {
  const uri = `${baseUri}/${tripId}/generate-alternative-item`
  return request
    .post<RegenerateItemAiResponse, RegenerateItemAiRequest>(uri, params, { ...getOptions(accessToken) })
    .then((response) => response.result.activities)
}

export function addBasicActivityToTrip(
  params: AddBasicActivityToTripRequest,
  tripId: string,
  accessToken?: string,
): Promise<AddBasicActivityToTripResponse['result']> {
  const uri = `${baseUri}/${tripId}/basic-activity`
  return request
    .post<AddBasicActivityToTripResponse, AddBasicActivityToTripRequest>(uri, params, { ...getOptions(accessToken) })
    .then((response) => response.result)
}

type ActivityLocationImage = {
  id: string
  name: string
  placeId: string
  imageIds: Array<string>
}

export function getLocationImage(
  placeId: string,
): Promise<Array<ActivityLocationImage>> {
  const uri = `${baseUri}/activity-location-images/${placeId}`
  return request
    .get<{ result: Array<ActivityLocationImage> }>(uri, getOptions())
    .then((response) => response.result.map(item => ({
      id: item.id,
      name: item.name,
      placeId: item.placeId,
      imageIds: item.imageIds,
    })))
}

export interface RegenerateDayAiParams {
  tripId: string
  date: string
  userInput?: string
  alternativeGroup?: string
  itemId?: string
}

export function regenerateDayAi(
  params: RegenerateDayAiParams,
  accessToken?: string,
): Promise<RegenerateDayAiResponse['result']> {
  const uri = `${baseUri}/regenerate-day`
  return request
    .post<RegenerateDayAiResponse, RegenerateDayAiRequest>(
      uri,
      params,
      { ...getOptions(accessToken), timeout: 120000 },
    )
    .then((response) => response.result)
}

export function undoRegenerateDayAi(
  params: UndoRegenerateDayAiRequest,
  accessToken?: string,
): Promise<UndoRegenerateDayAiResponse['result']> {
  const uri = `${baseUri}/undo-regenerate-day`
  return request
    .post<UndoRegenerateDayAiResponse, UndoRegenerateDayAiRequest>(
      uri,
      params,
      { ...getOptions(accessToken), timeout: 120000 },
    )
    .then((response) => response.result)
}

export function updateTravelPreferences(
  params: UpdateTravelPreferencesRequest,
  accessToken?: string,
): Promise<{ message: string } | null> {
  const uri = `${baseUri}/travel-preferences`
  return request
    .post<UpdateTravelPreferencesResponse, UpdateTravelPreferencesRequest>(
      uri,
      params,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}
export function createTrip(
  { name, startDate, endDate, imageId }: CreateArgs,
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  return request
    .post<CreateTripResponse, CreateTripRequest>(
      baseUri,
      {
        name,
        originPlaceId: SYDNEY_PLACE_INFO.id, // TODO: remove this param from the API
        startDate: startDate?.format(ISO_DATE_FORMAT),
        endDate: endDate?.format(ISO_DATE_FORMAT),
        imageId,
      },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

interface UpdateArgs {
  tripId: string
  name?: string
  startDate?: Moment | undefined | null
  endDate?: Moment | undefined | null
  imageId?: string
  originPlaceId?: string | null
  destinationPlaceIds?: Array<string>
  travellerRooms?: Array<TravellerRoom>
}

export function updateTrip(
  args: UpdateArgs,
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const {
    tripId,
    name,
    imageId,
    originPlaceId,
    destinationPlaceIds,
    travellerRooms,
  } = args

  const uri = `${baseUri}/${tripId}`

  let startDate: string | null | undefined = undefined
  if (Object.prototype.hasOwnProperty.call(args, 'startDate')) {
    startDate = args.startDate ? args.startDate.format(ISO_DATE_FORMAT) : null
  }

  let endDate: string | null | undefined = undefined
  if (Object.prototype.hasOwnProperty.call(args, 'endDate')) {
    endDate = args.endDate ? args.endDate.format(ISO_DATE_FORMAT) : null
  }

  return request
    .patch<UpdateTripResponse, UpdateTripRequest>(
      uri,
      {
        name,
        startDate,
        endDate,
        imageId,
        originPlaceId,
        destinationPlaceIds,
        travellerRooms,
      },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function fetchUser(
  {
    email,
  }: {
    email: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `/api/users/${email}`
  return request
    .get<App.ApiResponse<null>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function uploadTripImage(
  {
    tripId,
    imageData,
  }: {
    tripId: string
    imageData: string
  },
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const uri = `${baseUri}/${tripId}/trip-image`
  return request
    .post<UploadTripImageResponse, UploadTripImageRequest>(
      uri,
      {
        imageData,
      },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export async function getLowStockItemsForTrip(
  {
    tripId,
  }: {
    tripId: string
  },
  accessToken?: string,
): Promise<Array<definitions['lowStockItem']>> {
  const uri = `${baseUri}/${tripId}/low-stock-items`

  return request
    .get<GetLowStockTripItemsResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function uploadFile(
  file: File,
  accessToken?: string,
): Promise<UploadFileResponse> {
  const uri = '/api/trips/v2/files'
  const formData = new FormData()
  formData.append('file', file)

  return request
    .upload<App.ApiResponse<UploadFileResponse>>({
      path: uri,
      data: formData,
      options: getOptions(accessToken),
    })
    .then((response) => response.result)
}

export function getChat(
  chatId: number,
  region: string,
  accessToken?: string,
): Promise<TripPlannerChatDetails> {
  const uri = `${baseUri}/v2/chats/${chatId}?region=${encodeURIComponent(region)}`
  return request
    .get<App.ApiResponse<TripPlannerChatDetails>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export type ChatSurfaceHistory = operations['chatSurfaces']['responses'][200]['content']['application/json']['result']
export function getChatSurfaces(chatId: number, region: string): Promise<ChatSurfaceHistory> {
  return request.get<App.ApiResponse<ChatSurfaceHistory>>(
    `${baseUri}/v2/chats/${chatId}/surfaces?region=${encodeURIComponent(region)}`, getOptions(),
  ).then(response => response.result)
}

export function listChats(accessToken?: string): Promise<ChatListResponse> {
  const uri = `${baseUri}/v2/chats`
  return request
    .get<App.ApiResponse<ChatListResponse>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function searchChats(
  query: string,
  limit = 20,
  offset = 0,
  accessToken?: string,
): Promise<ChatSearchPageResponse> {
  const searchParams = new URLSearchParams({
    q: query,
    limit: String(limit),
    offset: String(offset),
  })
  const uri = `${baseUri}/v2/chats/search?${searchParams.toString()}`
  return request
    .get<ChatSearchPageResponse>(uri, getOptions(accessToken))
}

export function listPromptPresets(accessToken?: string): Promise<Array<PromptPresetResponse>> {
  const uri = `${baseUri}/v2/prompts`
  return request
    .get<App.ApiResponse<Array<PromptPresetResponse>>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function createPromptPreset(
  body: { name: string; promptId: string; notes?: string },
  accessToken?: string,
): Promise<PromptPresetResponse> {
  const uri = `${baseUri}/v2/prompts`
  return request
    .post<App.ApiResponse<PromptPresetResponse>, typeof body>(uri, body, getOptions(accessToken))
    .then((response) => response.result)
}

export function updatePromptPreset(
  promptId: string,
  body: { name: string; promptId: string; notes?: string },
  accessToken?: string,
): Promise<PromptPresetResponse> {
  const uri = `${baseUri}/v2/prompts/${promptId}`
  return request
    .put<App.ApiResponse<PromptPresetResponse>, typeof body>(uri, body, getOptions(accessToken))
    .then((response) => response.result)
}

export function deletePromptPreset(promptId: string, accessToken?: string): Promise<null> {
  const uri = `${baseUri}/v2/prompts/${promptId}`
  return request
    .delete<App.ApiResponse<null>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function deleteChat(
  chatId: number,
  accessToken?: string,
): Promise<DeleteChatResponse> {
  const uri = `${baseUri}/v2/chats/${chatId}`
  return request
    .delete<App.ApiResponse<DeleteChatResponse>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function switchDefaultTransport(
  itemId: number,
  accessToken?: string,
): Promise<SwitchDefaultTransportResponse> {
  const uri = `${baseUri}/v2/items/${itemId}/default-transport`
  return request
    .patch<App.ApiResponse<SwitchDefaultTransportResponse>, undefined>(uri, undefined, getOptions(accessToken))
    .then((response) => response.result)
}

export function updateTripChatItem(
  itemId: number,
  body: UpdateTripChatItemRequest,
  accessToken?: string,
): Promise<UpdateTripChatItemResponse> {
  const uri = `${baseUri}/v2/items/${itemId}`
  return request
    .patch<App.ApiResponse<UpdateTripChatItemResponse>, UpdateTripChatItemRequest>(uri, body, getOptions(accessToken))
    .then((response) => response.result)
}

export function claimChats(
  params: ClaimChatsRequest,
  accessToken?: string,
): Promise<ClaimChatsResponse> {
  const uri = `${baseUri}/v2/chats/claim`
  return request
    .post<App.ApiResponse<ClaimChatsResponse>, ClaimChatsRequest>(uri, params, getOptions(accessToken))
    .then((response) => response.result)
}

function buildGenerateChatTitleRequest(params: GenerateChatTitleParams): GenerateChatTitleRequest {
  if (!('surface' in params)) {
    return {
      message: params.message,
    }
  }

  switch (params.surface) {
    case 'trip_planner':
      return {
        message: params.message,
        surface: params.surface,
      }
    case 'accommodation_offer_page':
      return {
        message: params.message,
        surface: params.surface,
        surfaceContext: params.surfaceContext,
      }
    default:
      return assertUnreachable(params)
  }
}

export function generateChatTitle(
  params: GenerateChatTitleParams,
  accessToken?: string,
): Promise<GenerateChatTitleResponse> {
  const uri = `${baseUri}/v2/chats/${params.chatId}/title`
  return request
    .post<App.ApiResponse<GenerateChatTitleResponse>, GenerateChatTitleRequest>(
      uri,
      buildGenerateChatTitleRequest(params),
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function generateSalesAgentSummary(
  chatId: number,
  accessToken?: string,
): Promise<GenerateSalesAgentSummaryResponse> {
  const uri = `${baseUri}/v2/chats/${chatId}/sales-summary`
  return request
    .post<App.ApiResponse<GenerateSalesAgentSummaryResponse>, Record<string, never>>(
      uri,
      {},
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function updateChat(
  params: { chatId: number } & UpdateChatRequest,
  accessToken?: string,
): Promise<UpdateChatResponse> {
  const { chatId, ...payload } = params
  const uri = `${baseUri}/v2/chats/${chatId}`
  return request
    .patch<App.ApiResponse<UpdateChatResponse>, UpdateChatRequest>(uri, payload, getOptions(accessToken))
    .then((response) => response.result)
}

export function upsertTripChatRecentSearch(
  chatId: number,
  body: UpsertTripChatRecentSearchBody,
  accessToken?: string,
): Promise<TripChatRecentSearch> {
  const uri = `${baseUri}/v2/chats/${chatId}/recent-searches`
  return request
    .put<App.ApiResponse<TripChatRecentSearch>, UpsertTripChatRecentSearchBody>(
      uri,
      body,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function deleteManualItineraryTarget(
  params: {
    chatId: number
    region: string
    target: ManualItineraryDeleteRequest
  },
  accessToken?: string,
): Promise<ManualItineraryDeleteResponse> {
  const uri = `${baseUri}/v2/chats/${params.chatId}/manual-itinerary-delete?region=${encodeURIComponent(params.region)}`
  return request
    .post<App.ApiResponse<ManualItineraryDeleteResponse>, ManualItineraryDeleteRequest>(
      uri,
      params.target,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function moveManualItineraryItems(
  params: {
    chatId: number
    region: string
    request: ManualItineraryMoveRequest
  },
  accessToken?: string,
): Promise<ManualItineraryMoveResponse> {
  const uri = `${baseUri}/v2/chats/${params.chatId}/manual-itinerary-move?region=${encodeURIComponent(params.region)}`
  return request
    .post<App.ApiResponse<ManualItineraryMoveResponse>, ManualItineraryMoveRequest>(
      uri,
      params.request,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function getItinerary(
  chatId: number,
  region: string,
  accessToken?: string,
): Promise<TripItineraryData> {
  const uri = `${baseUri}/v2/chats/${chatId}/itinerary?region=${encodeURIComponent(region)}`
  return request
    .get<GetItineraryResponse>(uri, getOptions(accessToken))
    .then((response) => toTripItineraryData(response.result))
}

export function getPlaceDetail(
  googlePlaceId: string,
  accessToken?: string,
): Promise<GetPlaceDetailResult> {
  const uri = `${baseUri}/v2/places/${googlePlaceId}`
  return request
    .get<GetPlaceDetailResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export interface DeletePlaceCacheResponse {
  status: number
  result: {
    deleted: number
  }
}

export function deletePlaceCache(
  googlePlaceId?: string,
  accessToken?: string,
): Promise<{ deleted: number }> {
  const uri = googlePlaceId
    ? `${baseUri}/v2/admin/place-cache/${googlePlaceId}`
    : `${baseUri}/v2/admin/place-cache`
  return request
    .delete<DeletePlaceCacheResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

// Place Detail Section Endpoints - for progressive loading

interface PlaceSectionParams {
  googlePlaceId: string
  chatId?: number
}

export function getPlaceOverview(
  { googlePlaceId, chatId }: PlaceSectionParams,
  accessToken?: string,
): Promise<GetPlaceOverviewResult> {
  const params = chatId !== undefined ? `?chatId=${chatId}` : ''
  const uri = `${baseUri}/v2/places/${googlePlaceId}/overview${params}`
  return request
    .get<App.ApiResponse<GetPlaceOverviewResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getPlaceBestTimeToVisit(
  { googlePlaceId, chatId }: PlaceSectionParams,
  accessToken?: string,
): Promise<GetPlaceBestTimeToVisitResult> {
  const params = chatId !== undefined ? `?chatId=${chatId}` : ''
  const uri = `${baseUri}/v2/places/${googlePlaceId}/best-time-to-visit${params}`
  return request
    .get<App.ApiResponse<GetPlaceBestTimeToVisitResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getPlaceWhatToLove(
  { googlePlaceId, chatId }: PlaceSectionParams,
  accessToken?: string,
): Promise<GetPlaceWhatToLoveResult> {
  const params = chatId !== undefined ? `?chatId=${chatId}` : ''
  const uri = `${baseUri}/v2/places/${googlePlaceId}/what-to-love${params}`
  return request
    .get<App.ApiResponse<GetPlaceWhatToLoveResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getPlaceAreas(
  { googlePlaceId, chatId }: PlaceSectionParams,
  accessToken?: string,
): Promise<GetPlaceAreasResult> {
  const params = chatId !== undefined ? `?chatId=${chatId}` : ''
  const uri = `${baseUri}/v2/places/${googlePlaceId}/areas${params}`
  return request
    .get<App.ApiResponse<GetPlaceAreasResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getPlaceInsiderTips(
  { googlePlaceId, chatId }: PlaceSectionParams,
  accessToken?: string,
): Promise<GetPlaceInsiderTipsResult> {
  const params = chatId !== undefined ? `?chatId=${chatId}` : ''
  const uri = `${baseUri}/v2/places/${googlePlaceId}/insider-tips${params}`
  return request
    .get<App.ApiResponse<GetPlaceInsiderTipsResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getPlaceInfo(
  googlePlaceId: string,
  accessToken?: string,
  imageMode: PlaceInfoImageMode = 'preview',
): Promise<GetPlaceInfoResult> {
  const params = new URLSearchParams({ imageMode })
  const uri = `${baseUri}/v2/places/${googlePlaceId}/info?${params.toString()}`
  return request
    .get<App.ApiResponse<GetPlaceInfoResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function resolvePlaceSearch(searchQuery: string, accessToken?: string, signal?: AbortSignal): Promise<definitions['placeResolveData']> {
  const params = new URLSearchParams()
  params.append('query', searchQuery.trim())
  const uri = `${baseUri}/v2/places/resolve?${params.toString()}`
  return request.get<App.ApiResponse<definitions['placeResolveData']>>(uri, {
    ...getOptions(accessToken),
    signal,
  }).then((response) => response.result)
}

export async function streamPlaceHighlights(
  googlePlaceId: string,
  accessToken?: string,
  onChunk?: (chunk: string) => void,
  signal?: AbortSignal,
  locale?: string,
): Promise<void> {
  const path = `${baseUri}/v2/places/${googlePlaceId}/highlights`

  const isSSR = typeof window === 'undefined'
  const apiHost = isSSR ? (config.SERVER_SIDE_API_HOST ?? config.API_HOST) : config.API_HOST
  const endpoint = apiHost.startsWith('http') ? apiHost : `${config.SCHEME}://${apiHost}`
  const uri = `${endpoint}${path}`

  const options = getOptions(accessToken)

  const headers: HeadersInit = {
    ...(options.headers ?? {}),
    ...(locale ? { 'Accept-Language': locale } : undefined),
  }

  const res = await fetch(uri, {
    method: 'GET',
    headers,
    credentials: options.credentials ?? 'include',
    signal,
  })

  if (!res.ok) {
    const errorText = await res.text().catch(() => '')
    throw new Error(extractApiErrorMessage(errorText, res.status))
  }

  if (!res.body) {
    throw new Error('Response body is null')
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')

  try {
    while (true) {
      const { value, done } = await reader.read()

      if (done) break
      if (!value) continue

      const chunk = decoder.decode(value, { stream: true })
      if (chunk && onChunk) {
        onChunk(chunk)
      }
    }

    const finalChunk = decoder.decode()
    if (finalChunk && onChunk) {
      onChunk(finalChunk)
    }
  } finally {
    reader.releaseLock?.()
  }
}

export function getLocationDrawImages(
  params: LocationDrawImagesRequest,
  accessToken?: string,
): Promise<LocationDrawImagesResponse['result']> {
  const uri = `${baseUri}/v2/location-draw-images`
  return request
    .post<LocationDrawImagesResponse, LocationDrawImagesRequest>(
      uri,
      params,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function getPlaceDrawDetails(
  params: PlaceDrawDetailsRequest,
  accessToken?: string,
): Promise<PlaceDrawDetailsResponse['result']> {
  const uri = `${baseUri}/v2/place-draw-details`
  return request
    .post<PlaceDrawDetailsResponse, PlaceDrawDetailsRequest>(
      uri,
      params,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export async function createLocationDrawInformationStream(
  params: LocationDrawInformationRequest,
  accessToken?: string,
  onChunk?: (chunk: string) => void,
  signal?: AbortSignal,
  locale?: string,
): Promise<void> {
  const path = `${baseUri}/v2/location-draw-information`

  const isSSR = typeof window === 'undefined'
  const apiHost = isSSR ? (config.SERVER_SIDE_API_HOST ?? config.API_HOST) : config.API_HOST
  const endpoint = apiHost.startsWith('http') ? apiHost : `${config.SCHEME}://${apiHost}`
  const uri = `${endpoint}${path}`

  const options = getOptions(accessToken)

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers ?? {}),
    ...(locale ? { 'Accept-Language': locale } : undefined),
  }

  const res = await fetch(uri, {
    method: 'POST',
    body: JSON.stringify(params),
    headers,
    credentials: options.credentials ?? 'include',
    signal,
  })

  if (!res.ok) {
    const errorText = await res.text().catch(() => '')
    throw new Error(extractApiErrorMessage(errorText, res.status))
  }

  if (!res.body) {
    throw new Error('Response body is null')
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')

  try {
    while (true) {
      const { value, done } = await reader.read()

      if (done) break
      if (!value) continue

      const chunk = decoder.decode(value, { stream: true })
      if (chunk && onChunk) {
        onChunk(chunk)
      }
    }

    const finalChunk = decoder.decode()
    if (finalChunk && onChunk) {
      onChunk(finalChunk)
    }
  } finally {
    reader.releaseLock?.()
  }
}

export type HotelSearchParams = Omit<
  paths['/api/trips/v2/search/hotels/page']['get']['parameters']['query'],
  'occupancies'
> & {
  occupancies?: Array<App.Occupants>
}

export function searchHotels(
  params: HotelSearchParams,
): Promise<HotelSearchResult> {
  const queryParams = new URLSearchParams()
  queryParams.set('query', params.query)
  if (params.placeId) queryParams.set('placeId', params.placeId)
  if (params.propertyId) queryParams.set('propertyId', params.propertyId)
  if (params.typeaheadId) queryParams.set('typeaheadId', params.typeaheadId)
  if (params.region) queryParams.set('region', params.region)
  if (params.checkIn) queryParams.set('checkIn', params.checkIn)
  if (params.checkOut) queryParams.set('checkOut', params.checkOut)
  if (params.flexibleMonths) queryParams.set('flexibleMonths', params.flexibleMonths)
  if (params.flexibleNights) queryParams.set('flexibleNights', params.flexibleNights)
  serialiseOccupancy(params.occupancies ?? []).forEach((occupancy) => queryParams.append('occupancies[]', occupancy))
  if (params.loadedCount !== undefined) queryParams.set('loadedCount', params.loadedCount.toString())
  if (params.limit !== undefined) queryParams.set('limit', params.limit.toString())
  const uri = `${baseUri}/v2/search/hotels/page?${queryParams.toString()}`
  return request
    .get<paths['/api/trips/v2/search/hotels/page']['get']['responses']['200']['content']['application/json']>(uri, getOptions())
    .then((response) => response.result)
}

export const OFFER_PROPERTY_SURROUNDINGS_CATEGORIES: Array<App.OfferPropertySurroundingsCategory> = [
  'nearby',
  'restaurants_cafes',
  'top_attractions',
  'beach',
]

/** Google Places primary types excluded via svc-trip `excludeTypes`. */
export const OFFER_PROPERTY_SURROUNDINGS_EXCLUDE_PRIMARY_TYPES = [
  'fast_food_restaurant',
] as const

/** Request English-preferring surroundings names via svc-trip `englishOnly`. */
export const OFFER_PROPERTY_SURROUNDINGS_ENGLISH_ONLY = true

type ContractOfferPropertySurroundings = GetOfferPropertySurroundingsResponse['result']

function mapOfferPropertySurroundings(
  surroundings: ContractOfferPropertySurroundings,
): App.OfferPropertySurroundings {
  return {
    offerId: surroundings.offerId,
    coordinates: surroundings.coordinates,
    groups: surroundings.groups.map((group) => ({
      category: group.category,
      places: group.places.map((place) => ({
        id: place.id,
        name: place.name,
        distanceM: place.distanceM,
        coordinates: place.coordinates,
        primaryTypeDisplayName: place.primaryTypeDisplayName,
      })),
    })),
  }
}

interface GetOfferPropertySurroundingsParams {
  offerId: string
  region: string
  categories?: Array<App.OfferPropertySurroundingsCategory>
  limit?: number
  excludeTypes?: Array<string>
  englishOnly?: boolean
}

export function getOfferPropertySurroundings(
  params: GetOfferPropertySurroundingsParams,
): Promise<App.OfferPropertySurroundings> {
  const queryParams = new URLSearchParams({
    region: params.region,
  })
  if (params.categories?.length) queryParams.set('categories', params.categories.join(','))
  if (params.limit !== undefined) queryParams.set('limit', params.limit.toString())
  if (params.excludeTypes?.length) {
    queryParams.set('excludeTypes', params.excludeTypes.join(','))
  }
  if (params.englishOnly) queryParams.set('englishOnly', 'true')

  const uri = `${baseUri}/v2/offers/${encodeURIComponent(params.offerId)}/surroundings?${queryParams.toString()}`
  return request
    .get<GetOfferPropertySurroundingsResponse>(uri, getOptions())
    .then((response) => mapOfferPropertySurroundings(response.result))
}

interface GetOfferDetailParams {
  offerId: string
  region: string
  offerType?: string
  accommodationPropertyId?: string
  checkIn?: string
  checkOut?: string
  occupancies?: string
  flexibleMonths?: string
  flexibleNights?: string
  orderId?: string
}

export function getOfferDetail(params: GetOfferDetailParams): Promise<OfferDetailResult> {
  const queryParams = new URLSearchParams()
  queryParams.set('region', params.region)
  if (params.offerType) queryParams.set('offerType', params.offerType)
  if (params.accommodationPropertyId) queryParams.set('accommodationPropertyId', params.accommodationPropertyId)
  if (params.checkIn) queryParams.set('checkIn', params.checkIn)
  if (params.checkOut) queryParams.set('checkOut', params.checkOut)
  if (params.occupancies) queryParams.set('occupancies', params.occupancies)
  if (params.flexibleMonths) queryParams.set('flexibleMonths', params.flexibleMonths)
  if (params.flexibleNights) queryParams.set('flexibleNights', params.flexibleNights)
  if (params.orderId) queryParams.set('orderId', params.orderId)
  const uri = `${baseUri}/v2/offers/${params.offerId}?${queryParams.toString()}`
  return request
    .get<OfferDetailResponse>(uri, getOptions())
    .then((response) => response.result)
}

export function getHotelRoomOptions(
  offerId: string,
  params: HotelRoomOptionsRequest,
): Promise<HotelRoomOptionsResult> {
  const uri = `${baseUri}/v2/offers/${offerId}/room-options`
  return request
    .post<HotelRoomOptionsResponse, HotelRoomOptionsRequest>(uri, params, getOptions())
    .then((response) => response.result)
}

export function getExperienceDetail(
  offerId: string,
  currencyCode?: string,
): Promise<ExperienceDetailResult> {
  const queryParams = new URLSearchParams()
  if (currencyCode) {
    queryParams.set('currencyCode', currencyCode)
  }
  const uri = `${baseUri}/v2/experience-offers/${offerId}?${queryParams.toString()}`
  return request
    .get<ExperienceDetailResponse>(uri, getOptions())
    .then((response) => response.result)
}

export function searchExperiences(
  query: string,
  region?: string,
  fallbackPlace?: ExperienceSearchFallbackPlace,
): Promise<ExperienceSearchResult> {
  const queryParams = new URLSearchParams()
  queryParams.set('query', query)
  if (region) {
    queryParams.set('region', region)
  }
  if (fallbackPlace) {
    queryParams.set('fallbackPlaceId', fallbackPlace.placeId)
    queryParams.set('fallbackPlaceName', fallbackPlace.name)
    if (fallbackPlace.type) {
      queryParams.set('fallbackPlaceType', fallbackPlace.type)
    }
  }
  const uri = `${baseUri}/v2/search/experiences?${queryParams.toString()}`
  return request
    .get<ExperienceSearchResponse>(uri, getOptions())
    .then((response) => ({
      experiences: response.result,
      searchContext: response.searchContext,
    }))
}

export function getOfferReviews(
  offerId: string,
  region: string,
): Promise<OfferReviewsResult> {
  const queryParams = new URLSearchParams()
  queryParams.set('region', region)
  const uri = `${baseUri}/v2/offers/${offerId}/reviews?${queryParams.toString()}`
  return request
    .get<OfferReviewsResponse>(uri, getOptions())
    .then((response) => response.result)
}
